import { useCallback, useState } from 'react';
import type { FileId, Message, UserId } from '@cyphex/types';
import { lastEditableFrom } from '../lib/lastOwnMessage';
import type { MessageActions } from './useMessageActions';
import type { SendInput } from './useSendMessage';

interface Params {
  messages: readonly Message[];
  meId: UserId | null;
  actions: MessageActions;
  attachments: { readyFileIds: FileId[]; clear: () => void };
  send: (input: SendInput) => void;
  stopTyping: () => void;
}

export interface ThreadComposer {
  replyTo: Message | null;
  editing: Message | null;
  setReplyTo: (message: Message | null) => void;
  setEditing: (message: Message | null) => void;
  cancelContext: () => void;
  submit: (payload: { text: string; scheduledFor?: string }) => void;
  /** Up-arrow handler. `false` = nothing to edit, let the keystroke through. */
  editLast: () => boolean;
}

/**
 * Everything the composer needs to know about what it is currently doing:
 * replying, editing, or starting fresh.
 *
 * Extracted so `ThreadPane` stays a shell. `editLast` lives here rather than in
 * the composer because only this layer can see the timeline — the composer
 * knows its own text and nothing else.
 */
export function useThreadComposer({
  messages,
  meId,
  actions,
  attachments,
  send,
  stopTyping,
}: Params): ThreadComposer {
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [editing, setEditing] = useState<Message | null>(null);

  const cancelContext = useCallback(() => {
    setReplyTo(null);
    setEditing(null);
  }, []);

  const editLast = useCallback((): boolean => {
    const target = lastEditableFrom(messages, meId);
    if (!target) return false;
    setEditing(target);
    return true;
  }, [meId, messages]);

  const submit = useCallback(
    (payload: { text: string; scheduledFor?: string }) => {
      if (editing) {
        actions.edit(editing.id, payload.text);
        setEditing(null);
        return;
      }
      send({
        text: payload.text,
        fileIds: attachments.readyFileIds,
        replyToId: replyTo?.id ?? null,
        scheduledFor: payload.scheduledFor ?? null,
      });
      attachments.clear();
      setReplyTo(null);
      stopTyping();
    },
    [actions, attachments, editing, replyTo, send, stopTyping],
  );

  return { replyTo, editing, setReplyTo, setEditing, cancelContext, submit, editLast };
}
